import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import apiService from "../services/api";
import PageTransition from "../components/PageTransition";
import moment from "moment-timezone";
import "moment/locale/pt-br";
import DatePicker, { registerLocale } from "react-datepicker";
import { ptBR } from 'date-fns/locale';
import "react-datepicker/dist/react-datepicker.css";

// Configurar locale do DatePicker
registerLocale('pt-BR', ptBR);

// Configurar locale do moment globalmente
moment.locale('pt-br');

// Cache simples para dados de analytics
const analyticsCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

// Componente customizado para o input de data
const CustomDateInput = React.forwardRef(({ value, onClick, className }, ref) => (
  <div
    className={className}
    onClick={onClick}
    ref={ref}
  >
    {value || 'DD/MM/YYYY'}
  </div>
));

const Analytics = () => {
  const { user } = useAuth();
  useTheme();
  const [totalInteractions, setTotalInteractions] = useState(0);
  const [averageResponseTime, setAverageResponseTime] = useState(0);
  const [humanInterventions, setHumanInterventions] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [, setLastUpdated] = useState(null);

  // Estados para filtro de data
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const targetDbName = user?.target_db_name;

  // Função para verificar cache
  const getCachedData = useCallback((cacheKey) => {
    const cached = analyticsCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
    return null;
  }, []);

  // Função para salvar no cache
  const setCachedData = useCallback((cacheKey, data) => {
    analyticsCache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
  }, []);

  const loadAnalyticsData = useCallback(async (forceRefresh = false) => {
    if (!targetDbName) {
      setError("Usuário não possui target_db_name configurado");
      setLoading(false);
      return;
    }

    const cacheKey = `analytics_${targetDbName}_${startDate}_${endDate}_v3`;

    if (!forceRefresh) {
      const cachedData = getCachedData(cacheKey);
      if (cachedData) {
        setTotalInteractions(cachedData.totalInteractions);
        setAverageResponseTime(cachedData.averageResponseTime);
        setHumanInterventions(cachedData.humanInterventions);
        setChartData(cachedData.chartData);
        setLastUpdated(new Date());
        setLoading(false);
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout')), 10000)
      );

      const params = {};
      if (startDate) {
        params.startDate = startDate;
        params.endDate = endDate || startDate;
      }

      const dataPromises = [
        apiService.getInteractions(targetDbName, params),
        apiService.getResponseTime(targetDbName, params),
        apiService.getHumanInterventions(targetDbName, {}),
        apiService.getChartData(targetDbName, params),
      ];

      const [
        interactionsResponse,
        responseTimeResponse,
        interventionsResponse,
        chartResponse,
      ] = await Promise.race([
        Promise.all(dataPromises),
        timeoutPromise
      ]);

      const newTotalInteractions = interactionsResponse.total_interactions || 0;
      const newHumanInterventions = interventionsResponse.total_interventions || 0;
      const newAverageResponseTime = responseTimeResponse.average_response_time || 0;

      let formattedChartData = (chartResponse.data || []).map((item) => ({
        dia: item.formatted_date,
        interacoes: item.interactions || 0,
        intervencoes: item.human_interventions || 0,
      }));

      if (!startDate && !endDate) {
        formattedChartData = formattedChartData.slice(-7);
      }

      setTotalInteractions(newTotalInteractions);
      setHumanInterventions(newHumanInterventions);
      setAverageResponseTime(newAverageResponseTime);
      setChartData(formattedChartData);

      const dataToCache = {
        totalInteractions: newTotalInteractions,
        averageResponseTime: newAverageResponseTime,
        humanInterventions: newHumanInterventions,
        chartData: formattedChartData
      };
      setCachedData(cacheKey, dataToCache);
      setLastUpdated(new Date());

    } catch (err) {
      if (err.message === 'Timeout') {
        setError('Timeout ao carregar dados. Tente novamente.');
      } else {
        setError("Erro ao carregar dados das métricas");
      }
      setTotalInteractions(0);
      setAverageResponseTime(0);
      setChartData([]);
    } finally {
      setLoading(false);
    }
  }, [targetDbName, getCachedData, setCachedData, startDate, endDate]);

  useEffect(() => {
    loadAnalyticsData();
  }, [loadAnalyticsData]);

  // Memoizar dados para as métricas
  const metricas = useMemo(() => [
    {
      label: "Total de Interações",
      value: loading ? "..." : totalInteractions.toLocaleString("pt-BR"),
      cornerColor: "bg-primary/5",
    },
    {
      label: "Tempo de Resposta",
      value: loading ? "..." : `${averageResponseTime.toFixed(2)}s`,
      cornerColor: "bg-tertiary/5",
    },
    {
      label: "Intervenções Humanas",
      value: loading ? "..." : humanInterventions.toLocaleString("pt-BR"),
      cornerColor: "bg-error/5",
    },
  ], [loading, totalInteractions, averageResponseTime, humanInterventions]);

  // Função para gerar o título dinâmico
  const getDynamicTitle = () => {
    if (!startDate && !endDate) return "Métricas completas";

    try {
      const start = moment(startDate).locale('pt-br');
      const end = endDate ? moment(endDate).locale('pt-br') : start;

      if (!start.isValid()) return "Métricas";

      const startMonth = moment(start).startOf('month');
      const endMonth = moment(start).endOf('month');

      if (end.isValid() &&
        startDate === startMonth.format('YYYY-MM-DD') &&
        endDate === endMonth.format('YYYY-MM-DD')) {
        const monthName = start.format("MMMM");
        const capitalizedMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1);
        return `Métricas de ${capitalizedMonth} de ${start.format("YYYY")}`;
      }

      if (end.isValid() && start.isSame(end, 'day')) {
        return `Métricas do dia ${start.format("DD [de] MMMM [de] YYYY")}`;
      }

      if (end.isValid()) {
        return `Métricas do dia ${start.format("DD/MM/YYYY")} ao dia ${end.format("DD/MM/YYYY")}`;
      }

      return `Métricas a partir de ${start.format("DD/MM/YYYY")}`;

    } catch (e) {
      return "Métricas";
    }
  };

  // Handlers para mudança de data
  const handleStartDateChange = (date) => {
    if (!date) {
      setStartDate("");
      return;
    }
    const newStart = moment(date).format('YYYY-MM-DD');
    setStartDate(newStart);
    if (endDate && newStart > endDate) {
      setEndDate(newStart);
    }
  };

  const handleEndDateChange = (date) => {
    if (!date) {
      setEndDate("");
      return;
    }
    setEndDate(moment(date).format('YYYY-MM-DD'));
  };

  const clearFilters = () => {
    setStartDate("");
    setEndDate("");
  };

  // Calcular alturas normalizadas para o gráfico CSS
  const chartBars = useMemo(() => {
    if (!chartData.length) return [];
    const maxInteracoes = Math.max(...chartData.map(d => d.interacoes), 1);
    const maxIntervencoes = Math.max(...chartData.map(d => d.intervencoes), 1);
    const globalMax = Math.max(maxInteracoes, maxIntervencoes);
    return chartData.map((d, i) => ({
      dia: d.dia,
      interacoes: d.interacoes,
      intervencoes: d.intervencoes,
      interacoesHeight: Math.round((d.interacoes / globalMax) * 95),
      intervencoesHeight: Math.max(Math.round((d.intervencoes / globalMax) * 95), 2),
      isLast: i === chartData.length - 1,
    }));
  }, [chartData]);

  return (
    <PageTransition>
      <div className="flex-1 overflow-y-auto p-4 sm:p-8 space-y-6 sm:space-y-8">

        {/* Page Header */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-1">
            <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight font-headline text-on-surface">Métricas</h2>
            <p className="text-on-surface-variant font-medium font-body">Visualize e analise as métricas da IA em tempo real</p>
          </div>
        </div>

        {/* Date Filter Bar */}
        <section className="bg-surface-container rounded-xl p-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="material-symbols-outlined text-primary">calendar_month</span>
            <h2 className="text-base font-bold text-on-surface">
              {getDynamicTitle()}
            </h2>
          </div>
          <div className="flex items-center gap-2 bg-surface-container-highest p-1 rounded-lg">
            <span className="text-sm text-on-surface-variant px-2">Selecione a data</span>
            <div className="relative">
              <DatePicker
                selected={startDate ? moment(startDate).toDate() : null}
                onChange={handleStartDateChange}
                dateFormat="dd/MM/yyyy"
                locale="pt-BR"
                todayButton="Hoje"
                popperClassName="!z-[9999]"
                popperProps={{ strategy: "fixed" }}
                customInput={
                  <CustomDateInput
                    className="p-1.5 text-sm rounded cursor-pointer min-w-[100px] text-center text-on-surface bg-transparent"
                  />
                }
              />
            </div>
            <span className="text-on-surface-variant">-</span>
            <div className="relative">
              <DatePicker
                selected={endDate ? moment(endDate).toDate() : null}
                onChange={handleEndDateChange}
                dateFormat="dd/MM/yyyy"
                locale="pt-BR"
                minDate={startDate ? moment(startDate).toDate() : null}
                todayButton="Hoje"
                popperClassName="!z-[9999]"
                popperProps={{ strategy: "fixed" }}
                customInput={
                  <CustomDateInput
                    className="p-1.5 text-sm rounded cursor-pointer min-w-[100px] text-center text-on-surface bg-transparent"
                  />
                }
              />
            </div>
            {(startDate || endDate) && (
              <button
                onClick={clearFilters}
                className="p-1.5 hover:bg-surface-container rounded-md text-on-surface-variant transition-colors"
                title="Limpar filtros"
              >
                <span className="material-symbols-outlined text-base">filter_list_off</span>
              </button>
            )}
          </div>
        </section>

        {/* Mensagem de erro */}
        {error && (
          <section className="bg-error-container rounded-xl p-4">
            <p className="text-sm font-medium text-on-error-container">{error}</p>
          </section>
        )}

        {/* Metrics Top Grid */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {metricas.map((metrica, index) => (
            <div
              key={index}
              className="bg-surface-container glow-emerald rounded-xl border border-outline-variant/10 p-6 relative overflow-hidden group"
            >
              <div
                className={`absolute top-0 right-0 w-24 h-24 ${metrica.cornerColor} rounded-bl-full -mr-8 -mt-8 transition-transform group-hover:scale-110`}
              ></div>
              <div className="flex flex-col gap-4 relative z-10">
                <span className="text-xs font-bold text-on-surface-variant tracking-wider uppercase">
                  {metrica.label}
                </span>
                <span className="text-3xl font-black font-headline text-on-surface">
                  {metrica.value}
                </span>
              </div>
            </div>
          ))}
        </section>

        {/* Chart Section */}
        <section className="bg-surface-container glow-emerald rounded-xl border border-outline-variant/10 p-4 sm:p-8 flex flex-col gap-4 sm:gap-8 border-b-2 border-transparent focus-within:border-primary transition-all overflow-hidden">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h2 className="text-xl font-bold font-headline text-on-surface">Interações Diárias</h2>
              <p className="text-sm text-on-surface-variant">
                Volume de mensagens processadas nos últimos {chartData.length || 7} dias
              </p>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-secondary"></div>
                <span className="text-xs font-semibold text-on-surface-variant">IA Resolvido</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-error"></div>
                <span className="text-xs font-semibold text-on-surface-variant">Intervenção</span>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-[300px]">
              <div className="text-on-surface-variant">Carregando dados...</div>
            </div>
          ) : chartBars.length === 0 ? (
            <div className="flex items-center justify-center h-[300px]">
              <div className="text-on-surface-variant">Nenhum dado disponível</div>
            </div>
          ) : (
            /* Scroll horizontal quando há muitos dias */
            <div className="overflow-x-auto w-full -mx-1 px-1">
              <div
                className="flex items-end h-[220px] sm:h-[300px] pt-6 gap-2 group/chart"
                style={{
                  minWidth: chartBars.length > 7
                    ? `${chartBars.length * 56}px`
                    : '100%',
                  width: chartBars.length <= 7 ? '100%' : undefined,
                }}
              >
                {chartBars.map((bar, index) => (
                  <div
                    key={index}
                    className="flex flex-col items-center gap-2 h-full"
                    style={{
                      flex: chartBars.length <= 7 ? '1' : '0 0 48px',
                      minWidth: chartBars.length <= 7 ? '0' : '48px',
                    }}
                  >
                    <div className="w-full flex flex-col justify-end items-center gap-1 h-full">
                      {/* Números acima das barras */}
                      <div className="flex flex-col items-center gap-0.5 mb-0.5">
                        {bar.interacoes > 0 && (
                          <span className="text-[9px] sm:text-[10px] font-bold text-secondary leading-none whitespace-nowrap">
                            {bar.interacoes}
                          </span>
                        )}
                        {bar.intervencoes > 0 && (
                          <span className="text-[9px] sm:text-[10px] font-bold text-error leading-none whitespace-nowrap">
                            {bar.intervencoes}
                          </span>
                        )}
                      </div>
                      <div
                        className={`w-full max-w-[20px] rounded-t-sm hover:opacity-80 transition-opacity ${
                          bar.isLast
                            ? 'bg-secondary border-x border-secondary/40 shadow-[0_0_10px_rgba(5,102,217,0.4)]'
                            : 'bg-secondary'
                        }`}
                        style={{ height: `${bar.interacoesHeight}%` }}
                      ></div>
                      <div
                        className={`w-full max-w-[20px] rounded-b-sm hover:opacity-80 transition-opacity ${
                          bar.isLast
                            ? 'bg-error border-x border-error/40'
                            : 'bg-error'
                        }`}
                        style={{ height: `${bar.intervencoesHeight}%` }}
                      ></div>
                    </div>
                    <span
                      className={`text-[9px] font-bold whitespace-nowrap ${
                        bar.isLast ? 'text-primary' : 'text-on-surface-variant'
                      }`}
                    >
                      {bar.isLast ? 'HOJE' : bar.dia}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>


      </div>
    </PageTransition>
  );
};

export default Analytics;
